import { Gauge, Users } from 'lucide-react';

import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { cn } from '@/lib/utils';
import type { TrackedVehicle } from '@/types/transport';
import { ROUTE_TONE, TRIP_STATUS } from '../constants';

/**
 * Where each vehicle has reached on its run.
 *
 * The last of the section's screens still drawn as a plain panel rather than a
 * register: there is nothing here to search or page through, only what is
 * moving at this moment. When live positions arrive from the devices in the
 * vehicles, this is the component they feed.
 *
 * It was the tail of `TransportRegisters.tsx` until Routes, Vehicles, Drivers
 * and Students each grew a screen of their own and moved out, leaving this
 * alone in a file named after four things it is not.
 */
export function TrackingBoard({ vehicles }: { vehicles: TrackedVehicle[] }) {
  return (
    <section className="rounded-3xl border border-line bg-surface shadow-sm">
      <header className="border-b border-line px-4 py-3.5">
        <h2 className="text-base font-bold text-ink">On the road</h2>
        <p className="mt-0.5 text-sm text-ink-2">
          Where each vehicle has reached on its run. Updates arrive from the device in the vehicle.
        </p>
      </header>

      <ul className="grid grid-cols-1 gap-3 p-4 md:grid-cols-2 xl:grid-cols-3">
        {vehicles.map((vehicle, index) => (
          <li
            key={vehicle.uuid}
            className="rounded-2xl border border-line bg-surface-2 p-3.5 animate-fade-up"
            style={{ animationDelay: `${Math.min(index, 6) * 60}ms` }}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2.5 min-w-0">
                <span
                  className={cn('h-2.5 w-2.5 shrink-0 rounded-pill', ROUTE_TONE[vehicle.tone].dot)}
                  aria-hidden
                />
                <div className="min-w-0">
                  <p className="truncate font-bold text-ink">{vehicle.registrationNumber}</p>
                  <p className="truncate text-xs font-semibold text-ink-3">{vehicle.routeName}</p>
                </div>
              </div>
              <Badge tone={TRIP_STATUS[vehicle.status].tone} withDot>
                {TRIP_STATUS[vehicle.status].label}
              </Badge>
            </div>

            <div className="mt-3 flex items-center justify-between text-xs font-bold text-ink-2">
              <span className="inline-flex items-center gap-1">
                <Gauge className="h-3.5 w-3.5 text-ink-3" aria-hidden />
                {vehicle.speedKmph} km/h
              </span>
              <span>{vehicle.progress}% of the run</span>
            </div>
            <ProgressBar
              value={vehicle.progress}
              label={`${vehicle.registrationNumber} progress along ${vehicle.routeName}`}
              hideLabel
              size="sm"
              className="mt-1.5"
            />
          </li>
        ))}
      </ul>

      {vehicles.length === 0 && (
        <div className="p-4">
          <EmptyState
            icon={<Users className="h-6 w-6" />}
            title="Nothing on the road"
            description="Vehicles appear here once a morning run has started."
          />
        </div>
      )}
    </section>
  );
}
